'use client'

import Avatar from '@component/avatar'
import AgentsCarousel, { useAgentsCarousel } from '@component/card/agent/carousel'
import OfficeLine from '@component/card/listings/office/line'
import DisablePrefetchLink from '@component/disablePrefetchLink'
import Tooltip from '@component/tooltip'
import IconCopy from '@icon/house/copy.svg'
import { enquiryDataLayer } from '@plugin/google/gtm/modules/property'
import { statsHouseDetailPhoneClick } from '@plugin/statistics'
import { agentDetail, houseDetail } from '@router'
import { global } from '@store/index'
import { blockNumber, cn } from '@utils'
import { getHouseId, isVillageData, isVillageTypeUnitData } from '@utils/house'
import ClipboardJS from 'clipboard'
import { get, map, size, split } from 'lodash-es'
import { Suspense, useEffect, useRef } from 'react'
import { useBoolean } from 'react-use'
import { useRecoilValue } from 'recoil'
import 'swiper/swiper.css'
import AgentEnquiryButton from './enquiryButton'

export default function HouseAgents({
  data
}) {

  return (
    <div className='shadow-md rounded-sm overflow-hidden'>
      <OfficeLine data={data} className='!h-50' center iconHeight={32}></OfficeLine>
      <div className='p-20 pt-10 sm:py-30'>
        {
          isVillageData(data)
            ? <VillageCard data={data}></VillageCard>
            : (
              <AgentList data={data}></AgentList>
            )
        }
      </div>
    </div>
  )
}

function AgentList({
  data
}) {
  const agents = get(data, 'agents', [])
  const len = size(agents)
  return len > 0
    ? (
      <AgentsCarousel
        data={agents}
        rightSlot={
          <AgentName
            data={data}
            className={cn(
              'text-left hidden lg:block whitespace-nowrap',
              { 'w-142': len >= 2 }
            )}
          />
        }
      >
        <AgentOffice data={data} agentNameClassName='lg:hidden' />
      </AgentsCarousel>
    )
    : <AgentOffice data={data} agentNameClassName='lg:hidden' />
}

export function AgentOffice({
  data,
  agentNameClassName = '',
  showOfficeName = true
}) {
  const agentsCarouselContext = useAgentsCarousel()
  const { currentAgent } = agentsCarouselContext || {}
  return (
    <>
      <AgentName
        data={data}
        className={cn('text-center', agentNameClassName)}
      />
      {
        showOfficeName && (
          <div className='text-sm text-center mt-10'>
            {
              map(split(get(data, 'office.name'), '-'), (item, idx) => (
                <div key={idx}>{item}</div>
              ))
            }
          </div>
        )
      }
      <Suspense>
        <AgentEnquiryButton
          data={currentAgent}
          house={data}
          className='mt-20 !h-48 !text-base'
          sendAllAgent
        />
      </Suspense>
    </>
  )
}

function AgentName({
  data,
  className = ''
}) {
  const { isMobile } = useRecoilValue(global)
  const agentsCarouselContext = useAgentsCarousel()
  const { currentAgent } = agentsCarouselContext || {}

  const [showPhone, togglePhone] = useBoolean(false)
  const [copySuccess, toggleCopySuccess] = useBoolean(false)
  const copyRef = useRef(null)
  const timer = useRef(null)

  useEffect(() => {
    if (!copyRef.current || !showPhone) return

    const clipboard = new ClipboardJS(copyRef.current, {
      text: () => currentAgent.phoneNumber
    })
    clipboard.on('success', () => {
      toggleCopySuccess(true)
      clearTimeout(timer.current)
      timer.current = setTimeout(() => {
        toggleCopySuccess(false)
      }, 3000)
    })

    return () => {
      clipboard.destroy()
    }
  }, [currentAgent, copyRef, timer, showPhone, toggleCopySuccess])

  if (!currentAgent) {
    return null
  }

  function copyHandle(event) {
    event.preventDefault()
    event.stopPropagation()
    enquiryDataLayer.phoneCopy()
  }
  function handleDisplayPhone() {
    togglePhone(true)
    enquiryDataLayer.phoneClick()
    statsHouseDetailPhoneClick(get(currentAgent, 'id'), getHouseId(data))
  }

  return (
    <div className={cn('text-lg', className)}>
      <DisablePrefetchLink
        href={agentDetail(currentAgent)}
        target='_blank'
        className='whitespace-nowrap overflow-hidden text-ellipsis block'
      >
        {currentAgent.name}
      </DisablePrefetchLink>
      {
        currentAgent.phoneNumber && (
          <Tooltip
            zIndex={201}
            active={copySuccess}
            disabled
            bottom={isMobile}
            right={!isMobile}
            content={<div className='text-secondary text-md px-10 py-2'>Copied!</div>}
            className='max-w-full'
          >
            <a
              href={`tel:${currentAgent.phoneNumber}`}
              className='button-primary-out h-32 mt-8 w-full px-8'
              onClick={handleDisplayPhone}
              data-noprogress
            >
              {
                !showPhone && (
                  <i className='icon icon-phone text-primary mr-8'></i>
                )
              }
              <span className='text-ellipsis whitespace-nowrap overflow-hidden'>{showPhone ? currentAgent.phoneNumber : blockNumber(currentAgent.phoneNumber)}</span>
              {
                showPhone && (
                  <>
                    <i className='w-1 h-14 bg-primary ml-8'></i>
                    <button className='cursor-pointer h-full px-8 -mr-8' onClick={copyHandle} ref={copyRef}>
                      <IconCopy width={14} height={14} fill='var(--color-theme)' className='block'></IconCopy>
                    </button>
                  </>
                )
              }
            </a>
          </Tooltip>
        )
      }
    </div>
  )
}

function VillageCard({
  data
}) {
  const isVillageTypeUnit = isVillageTypeUnitData(data)

  return (
    <div className='flex flex-col items-center gap-y-20'>
      <Avatar
        src={data.villageLogoDetail}
        width={90}
        height={90}
        circle
        alt={data.villageContactName}
      />
      <DisablePrefetchLink
        href={houseDetail({ slug: isVillageTypeUnit ? data.villageParentSlug : data.slug })}
        className='text-font text-lg font-normal'
      >
        {data.villageContactName}
      </DisablePrefetchLink>
      <AgentEnquiryButton data={get(data, 'agents.0')} house={data} className='mt-20 !h-48 !text-base'></AgentEnquiryButton>
    </div>
  )
}
